import { getCompanyInfo } from '@/utils/getCompanyInfo';

/** 折扣规则名称最大长度（与创建页 Input maxLength 一致） */
export const COUPON_NAME_MAX_LENGTH = 100;

export function normalizeCouponName(couponName: string): string {
  return String(couponName || '').trim().slice(0, COUPON_NAME_MAX_LENGTH);
}

export function getCouponCompanyId(): number {
  return getCompanyInfo()?.id as number;
}

function stringifyScopeIds(ids: unknown): string {
  if (Array.isArray(ids)) {
    return JSON.stringify(ids);
  }
  if (typeof ids === 'string') {
    return ids;
  }
  return '';
}

function stringifyScopeItems(items: unknown): string {
  if (Array.isArray(items)) {
    return JSON.stringify(items);
  }
  if (typeof items === 'string') {
    return items;
  }
  return '';
}

function formatExpireDate(date: unknown): string | null {
  if (!date) return null;
  if (typeof date === 'string') return date;
  if (typeof date === 'object' && date !== null && 'format' in date && typeof (date as { format: (f: string) => string }).format === 'function') {
    return (date as { format: (f: string) => string }).format('YYYY-MM-DD');
  }
  const parsed = new Date(date as string | number | Date);
  if (Number.isNaN(parsed.getTime())) return null;
  const y = parsed.getFullYear();
  const m = `${parsed.getMonth() + 1}`.padStart(2, '0');
  const d = `${parsed.getDate()}`.padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function parseJsonArray(value: unknown): any[] {
  if (Array.isArray(value)) return value;
  if (!value) return [];
  try {
    const parsed = JSON.parse(String(value));
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return String(value)
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  }
}

export { parseJsonArray };

/** 弹窗 inline discount → 创建规则 API 参数 */
export function buildCouponRuleCommandFromPopDiscount(couponName: string, discount: Record<string, any>) {
  const isProductDiscount = discount.uniqueDiscountType === 'PRODUCT_AMOUNT_DISCOUNT';
  const applyScopeType = discount.applyScopeType === 'PRODUCTS' ? 'PRODUCTS' : 'COLLECTIONS';
  const applyScopeIds = stringifyScopeIds(discount.applyScopeIds);
  const applyScopeItems = stringifyScopeItems(discount.applyScopeItems);

  return {
    companyId: getCouponCompanyId(),
    couponName: normalizeCouponName(couponName),
    uniqueDiscountType: discount.uniqueDiscountType,
    codePrefix: discount.codePrefix || undefined,
    valueType: discount.valueType || 2,
    amount:
      discount.valueType === 1 && discount.amount !== '' && discount.amount != null
        ? String(discount.amount)
        : null,
    percent:
      discount.valueType === 2 && discount.percent !== '' && discount.percent != null
        ? Number(discount.percent)
        : null,
    minBuyRequireType: discount.minBuyRequireType ?? 0,
    minBuyAmount:
      discount.minBuyRequireType === 1 && discount.minBuyAmount !== '' && discount.minBuyAmount != null
        ? String(discount.minBuyAmount)
        : null,
    minBuyQuantity:
      discount.minBuyRequireType === 2 && discount.minBuyCount !== '' && discount.minBuyCount != null
        ? Number(discount.minBuyCount)
        : null,
    expireType: discount.expireType ?? 0,
    expireValue:
      discount.expireType === 1 && discount.expireValue !== '' && discount.expireValue != null
        ? Number(discount.expireValue)
        : null,
    expireValueType: discount.expireType === 1 ? discount.expireValueType || 'day' : null,
    expireDate: discount.expireType === 2 ? formatExpireDate(discount.expireDate) : null,
    combinesWithProduct: Boolean(discount.combinesWithProduct),
    combinesWithOrder: Boolean(discount.combinesWithOrder),
    combinesWithShipping: Boolean(discount.combinesWithShipping),
    applyCollectionsEnabled: isProductDiscount && applyScopeType === 'COLLECTIONS',
    applyCollectionIds: isProductDiscount && applyScopeType === 'COLLECTIONS' ? applyScopeIds : '',
    applyCollectionItems: isProductDiscount && applyScopeType === 'COLLECTIONS' ? applyScopeItems : '',
    applyProductsEnabled: isProductDiscount && applyScopeType === 'PRODUCTS',
    applyProductIds: isProductDiscount && applyScopeType === 'PRODUCTS' ? applyScopeIds : '',
    applyProductItems: isProductDiscount && applyScopeType === 'PRODUCTS' ? applyScopeItems : '',
    appliesOncePerOrder: discount.appliesOncePerOrder ?? true,
    appliesOnOneTimePurchase: true,
    appliesOnSubscription: true,
  };
}

/** 前端 UI 用字段，不提交后端 */
export function stripFrontendDiscountMeta<T extends Record<string, any>>(payload: T): T {
  const next = { ...payload };
  delete next.unifiedMode;
  delete next.linkType;
  return next;
}

/** 下拉/创建规则后，组装提交后端的 discount（保留原字段并写入 couponId） */
export function buildUnifiedPopDiscountPayload(
  base: Record<string, any> | null | undefined,
  couponId: string,
  reserveCode: string
) {
  return stripFrontendDiscountMeta({
    ...(base || {}),
    couponId,
    reserveCode: reserveCode.trim(),
  });
}

/** 规则详情 → 弹窗 discount 结构 */
export function mapCouponDetailToPopDiscount(
  detail: Record<string, any>,
  reserveCode = ''
) {
  const isProductDiscount = detail.uniqueDiscountType === 'PRODUCT_AMOUNT_DISCOUNT';
  let applyScopeType: 'COLLECTIONS' | 'PRODUCTS' = 'COLLECTIONS';
  let applyScopeIds: string[] = [];
  let applyScopeItems: any[] = [];

  if (detail.applyScopeType) {
    applyScopeType = detail.applyScopeType === 'PRODUCTS' ? 'PRODUCTS' : 'COLLECTIONS';
    applyScopeIds = parseJsonArray(detail.applyScopeIds);
    applyScopeItems = parseJsonArray(detail.applyScopeItems);
  } else if (isProductDiscount) {
    if (detail.applyProductsEnabled) {
      applyScopeType = 'PRODUCTS';
      applyScopeIds = parseJsonArray(detail.applyProductIds);
      applyScopeItems = parseJsonArray(detail.applyProductItems);
    } else if (detail.applyCollectionsEnabled) {
      applyScopeType = 'COLLECTIONS';
      applyScopeIds = parseJsonArray(detail.applyCollectionIds);
      applyScopeItems = parseJsonArray(detail.applyCollectionItems);
    }
  }

  return {
    uniqueDiscountType: detail.uniqueDiscountType,
    codePrefix: detail.codePrefix || '',
    valueType: detail.valueType,
    amount: detail.amount != null ? String(detail.amount) : '',
    percent: detail.percent != null ? String(detail.percent) : '',
    minBuyRequireType: detail.minBuyRequireType ?? 0,
    minBuyAmount: detail.minBuyAmount != null ? String(detail.minBuyAmount) : '',
    minBuyCount: detail.minBuyQuantity != null ? String(detail.minBuyQuantity) : '',
    applyScopeType,
    applyScopeIds,
    applyScopeItems,
    combinesWithProduct: Boolean(detail.combinesWithProduct),
    combinesWithOrder: Boolean(detail.combinesWithOrder),
    combinesWithShipping: Boolean(detail.combinesWithShipping),
    appliesOncePerOrder: detail.appliesOncePerOrder ?? true,
    expireType: detail.expireType ?? 0,
    expireValue: detail.expireValue != null ? detail.expireValue : '',
    expireValueType: detail.expireValueType || 'day',
    expireDate: detail.expireDate || '',
    reserveCode: reserveCode || detail.reserveCode || '',
    couponId: detail.couponId,
    couponName: detail.couponName || '',
  };
}

export function hasLegacyInlinePopDiscount(discount?: Record<string, any> | null) {
  if (!discount || discount.couponId || discount.unifiedMode) return false;
  return Boolean(
    discount.uniqueDiscountType ||
      discount.percent ||
      discount.amount ||
      discount.codePrefix ||
      discount.couponName
  );
}
